import Modal from 'react-bootstrap/Modal';

import { ButtonPrimary, Icon, ModalClose } from '../../common/components';
import { FAQModalWrapper } from './FAQStyles.js';
import { FAQAccordionItem } from '../faqmanagement/FAQAccordionItem';
import { useContext, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { toast } from 'react-toastify';
import {
  getFAQDocumentImageUrl,
  getFAQDocumentImageUrlAdmin,
  getFAQs,
  getFAQsAdmin,
} from '../../common/api/adminFaqApi';
import { internalNavigation } from './navigation/internalNavigation';
import { corporateNavigation } from './navigation/corporateNavigation';
import { corporateBrokerNavigation } from './navigation/corporateBrokerNavigation';
import { pageMenuName, trimAndLowerCase } from '../utils/text/text';

import * as colors from '../../common/styles/colors';
import * as variables from '../../common/styles/variables';

import { NavLink } from 'react-router-dom';
import { iconsToUse } from '../../common/styles/themes';
import { NavigationTemplateProps } from '../sidemenu/templates/NavigationTemplateProps';
import { BRANDS } from '../../consts';
import { useAuthState } from '../../providers/authProvider';
import { isTpiUser } from '../../helpers/tpiUser';
import { GlobalContext, GlobalDetailContextProps } from '../../context/globalContext/globalContext';

type FAQModalProps = {
  show: boolean;
  onClose: () => void;
};

const defaultProps = {
  show: true,
};

export const FAQModal = (props: FAQModalProps) => {
  const { show } = props;
  const { instance, accounts } = useMsal();
  const authContext = useAuthState();

  const globalContext = useContext<GlobalDetailContextProps>(GlobalContext);

  const [openAccordionItem, setOpenAccordionItem] = useState<Api.IFAQItem>();
  const [containsImage, setContainsImage] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<any>(undefined);

  const [faqItems, setFaqItems] = useState<Array<Api.IFAQItem>>([]);
  const [allFaqItems, setAllFaqItems] = useState<Array<Api.IFAQItem>>([]);

  const isOpen = (id: number) => {
    return id === openAccordionItem?.faqId;
  };

  const toggleItem = (item: Api.IFAQItem) => {
    if (openAccordionItem?.faqId === item.faqId) {
      setOpenAccordionItem(undefined); // untoggle Item
    } else {
      setOpenAccordionItem(item);
    }
  };

  const error = (error: string) => {
    const message = `API error calling: ${error}`;
    console.error(message);
    toast.error(message, { position: 'bottom-right' });
  };

  const imageSuccess = (blob: any) => {
    const objectUrl = URL.createObjectURL(blob);
    setImageSrc(objectUrl);
    setContainsImage(true);
  };

  const adminFAQsSuccess = (response: any) => {
    setAllFaqItems(response.data);
  };

  useEffect(() => {
    if (show) {
      if (process.env.REACT_APP_ADMIN_SITE === 'true') {
        getFAQsAdmin(accounts[0], instance, adminFAQsSuccess, error);
      } else {
        getFAQs(authContext)
          .then((response: Common.IResult<Array<Api.IFAQItem>>) => {
            if (response.success) {
              setAllFaqItems(response.data);
            }
          })
          .catch((e: any) => {
            error('getFAQs');
          });
      }
    }
  }, [show]);

  useEffect(() => {
    let pageName = pageMenuName();

    const isTpiCustomer = globalContext?.globalDetail?.showBrokerLandingPage;
    if (isTpiCustomer) {
      pageName = 'Select Customers';
    }

    onMenuLinkClicked(pageName);
  }, [allFaqItems]);

  useEffect(() => {
    setImageSrc(undefined);
    setContainsImage(false);

    if (openAccordionItem && isOpen(openAccordionItem.faqId) && openAccordionItem.faqDocumentId) {
      if (process.env.REACT_APP_ADMIN_SITE === 'true') {
        getFAQDocumentImageUrlAdmin(accounts[0], instance, openAccordionItem.faqDocumentId, imageSuccess, error);
      } else {
        getFAQDocumentImageUrl(openAccordionItem.faqDocumentId, authContext, imageSuccess, error);
      }
    }
  }, [openAccordionItem]);

  const navigationProps: NavigationTemplateProps = {
    brand: process.env.REACT_APP_BRAND as BRANDS,
    isExclusiveOffersDataAvailable: false,
    showMeterReadButtons: false,
    showSmartMeterConsent: false,
    isTpi: false,
  };

  let customerData: Array<Navigation.INavigationData> = [];

  if (process.env.REACT_APP_ADMIN_SITE === 'true') {
    customerData = internalNavigation(navigationProps);
  } else {
    const isTpiCustomer = globalContext.globalDetail.showBrokerLandingPage || isTpiUser();
    if (isTpiCustomer) {
      customerData = corporateBrokerNavigation(navigationProps);
    } else {
      customerData = corporateNavigation(navigationProps);
    }
  }

  const onMenuLinkClicked = (linkText: string, event?: React.MouseEvent<HTMLAnchorElement>) => {
    if (event) {
      event.preventDefault();
    }

    let audienceInternal = false;
    let audienceCustomer = false;
    let audienceTpi = false;

    if (process.env.REACT_APP_ADMIN_SITE === 'true') {
      audienceInternal = true;
    }

    const isTpiCustomer = globalContext?.globalDetail?.showBrokerLandingPage || isTpiUser();
    if (isTpiCustomer) {
      audienceTpi = true;
    } else {
      audienceCustomer = true;
    }

    const items = allFaqItems.filter(
      (item: any) =>
        item.faqCategory.toLowerCase() === linkText.toLowerCase() &&
        (item.faqAudience === 'All' ||
          (audienceCustomer && item.faqAudience === 'Customer') ||
          (audienceTpi && item.faqAudience === 'TPI') ||
          (audienceInternal && item.faqAudience === 'Internal'))
    );
    setFaqItems(items);
  };

  const navigationData: Array<Navigation.INavigationData> = customerData;

  const navigationItems = navigationData.map((item: any, index: number) => {
    const link = '/home' + item.link;

    return (
      <li key={index} className={`menu ${trimAndLowerCase(item.text)}`}>
        <NavLink to={link} className='menu-link' onClick={(event: any) => onMenuLinkClicked(item.text, event)}>
          <span className={`menu-icon ${item.classname}`}>
            <Icon color={colors.oxfordBlue} size={variables.iconSizeBase} icon={(iconsToUse as any)[`${item.icon}`]} />
          </span>
          <span className='menu-item'>{item.text}</span>
        </NavLink>
      </li>
    );
  });

  return (
    <>
      <Modal animation={false} className={'custom-modal'} show={show} size={'xl'} backdrop='static' centered>
        <FAQModalWrapper>
          <Modal.Header>
            <Modal.Title>Frequently Asked Questions</Modal.Title>
            <ModalClose onClick={props.onClose} />
          </Modal.Header>

          <Modal.Body>
            <div className='row'>
              <div className='col-3 menu-wrapper'>
                <div className='mainMenu'>{navigationItems}</div>
              </div>
              <div className='col-9'>
                {faqItems.map((item: Api.IFAQItem) => (
                  <FAQAccordionItem
                    item={item}
                    containsImage={containsImage}
                    imageSrc={imageSrc}
                    isOpen={isOpen(item.faqId)}
                    toggleItem={() => toggleItem(item)}
                  ></FAQAccordionItem>
                ))}
                {faqItems.length == 0 && <div className='pt-4 pl-4'>There are no items to display</div>}
              </div>
            </div>
          </Modal.Body>

          <div className='pt-3 float-right'>
            <ButtonPrimary
              onClick={event => {
                event.preventDefault();
                props.onClose();
              }}
              className='ml-4 upload-btn'
              title='Cancel'
            ></ButtonPrimary>
          </div>
        </FAQModalWrapper>
      </Modal>
    </>
  );
};

FAQModal.defaultProps = defaultProps;
